import { Habitacion, TiposExtras } from "src/gql/schema"
import { formatCurrency } from "src/shared/hooks/formatCurrency"
import { sum } from "src/shared/helpers/calculator"
import { groupRentasTarifasByTarifa } from "src/shared/helpers/groupRentasTarifasByTarifa"
import { groupExtrasTarifasByTarifa } from "src/shared/helpers/groupExtrasTarifasByTarifa"
import { useIVA } from "src/shared/hooks/useIVA"
import { capitalize } from "src/shared/helpers/capitalize"
import { useDate } from "src/shared/hooks/useDate"

export type EstanciaRow = {
    concepto: string
    tarifa: string
    cantidad: number
    total: string
    iva: string
    status: string
    noches?: number
    tipo_extra: TiposExtras | null
}

export const useGetEstanciaRows = (habitacion?: Habitacion): EstanciaRow[] => {
    const { getIVA } = useIVA()
    const { diffDays, UTCStringToLocalDate } = useDate()
    const renta = habitacion?.ultima_renta

    const habitacionRows: EstanciaRow[] = groupRentasTarifasByTarifa(renta?.rentas_tarifas || []).map((grupo) => ({
        concepto: "Habitación",
        tarifa: `${grupo.tarifa?.grupo_tarifa?.nombre || "-"}`,
        cantidad: sum(grupo.entradas.map((e) => e.subtotales?.noches || 0)),
        total: formatCurrency(grupo.total),
        iva: formatCurrency(getIVA(grupo.total)),
        status: "Pagado",
        tipo_extra: null,
    }))

    const checkInRow: EstanciaRow[] = renta?.subtotales?.total_early_checkin
        ? [{
            concepto: "Check-in anticipado",
            tarifa: `Normal ${formatCurrency(renta.subtotales.total_early_checkin)}`,
            cantidad: 1,
            total: formatCurrency(renta.subtotales.total_early_checkin),
            iva: formatCurrency(getIVA(renta.subtotales.total_early_checkin)),
            status: "Pagado",
            tipo_extra: null,
        }]
        : []

    const extrasRows: EstanciaRow[] = (renta?.extras || []).flatMap((extra) =>
        groupExtrasTarifasByTarifa(extra.extras_tarifas || []).map((grupo) => {

            const noches = extra.tipo_extra === TiposExtras.Persona
                ? (() => {
                    const fechas = grupo.entradas.map((e) => ({
                        inicio: UTCStringToLocalDate(e.fecha_inicio).getTime(),
                        fin: UTCStringToLocalDate(e.fecha_fin).getTime(),
                    }))
                    const minInicio = new Date(Math.min(...fechas.map((f) => f.inicio)))
                    const maxFin = new Date(Math.max(...fechas.map((f) => f.fin)))
                    return Math.max(1, diffDays(minInicio, maxFin))
                })()
                : undefined
            return {
                concepto: extra.tipo_extra === TiposExtras.Persona ? "Persona extra"
                    : extra.tipo_extra === TiposExtras.Hospedaje ? "Hospedaje extra"
                    : `${capitalize(extra.tipo_extra)} extra`,
                tarifa: `${grupo.tarifa?.grupo_tarifa?.nombre || ""}`,
                cantidad: Math.max(...grupo.entradas.map((e) => e.subtotales?.numero || 0)),
                total: formatCurrency(grupo.total),
                iva: formatCurrency(getIVA(grupo.total)),
                status: "Pagado",
                noches,
                tipo_extra: extra.tipo_extra,
            }
        })
    )

    const propinasTotal = (renta?.propinas ?? []).reduce((acc, p) => acc + (p?.total || 0), 0)
    const propinasRow: EstanciaRow[] = propinasTotal > 0
        ? [{
            concepto: "Propina",
            tarifa: "NA",
            cantidad: 1,
            total: formatCurrency(propinasTotal),
            iva: "NA",
            status: "Pagado",
            tipo_extra: null,
        }]
        : []

    return [...habitacionRows, ...checkInRow, ...extrasRows, ...propinasRow]
}
